import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import type { DefaultOpenProperty } from '@affine/core/components/properties';
import {
  EdgelessFrameManagerIdentifier,
  PresentTool,
} from '@blocksuite/affine/blocks/frame';
import {
  DefaultTool,
  EdgelessLegacySlotIdentifier,
} from '@blocksuite/affine/blocks/surface';
import type { DocTitle } from '@blocksuite/affine/fragments/doc-title';
import { findCommentedTexts } from '@blocksuite/affine/inlines/comment';
import type { DocMode, ReferenceParams } from '@blocksuite/affine/model';
import { HighlightSelection } from '@blocksuite/affine/shared/selection';
import {
  DocModeProvider,
  findCommentedBlocks,
  findCommentedElements,
  ViewportElementProvider,
} from '@blocksuite/affine/shared/services';
import type { EditorHost } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { Bound } from '@blocksuite/global/gfx';
import type { InlineEditor } from '@blocksuite/std/inline';
import { effect } from '@preact/signals-core';
import { Entity, LiveData } from '@toeverything/infra';
import { defaults, isEqual, omit, throttle } from 'lodash-es';
import { skip } from 'rxjs';

import { canAttemptBackendRequest } from '../../../utils/backend-availability';
import { CommentPanelService } from '../../comment/services/comment-panel-service';
import { DocCommentManagerService } from '../../comment/services/doc-comment-manager';
import type { DocService } from '../../doc';
import { DEFAULT_DOC_MODE } from '../../doc/constants';
import { paramsParseOptions, preprocessParams } from '../../navigation/utils';
import type { WorkspaceShareSettingService } from '../../share-setting';
import { DocViewStateService } from '../../streamlain/services/doc-view-state';
import { FullscreenRegistry } from '../../streamlain/services/fullscreen-registry';
import type { WorkbenchView } from '../../workbench';
import type { WorkspaceService } from '../../workspace';
import { EditorScope } from '../scopes/editor';
import { FollowerInputService } from '../services/follower-input';
import { FollowerViewportSyncService } from '../services/follower-viewport-sync';
import { FullscreenService } from '../services/fullscreen.service';
import { PresentationStateMachine } from '../services/presentation-state-machine';
import { WorkspaceRealtimeService } from '../services/workspace-realtime.service';
import {
  type EditorSelector,
  StateMachineHelpers,
  type ViewportState,
} from '../types';
import {
  createSavedFullscreenQueryPatch,
  isWorkbenchPathForDoc,
  mergeSavedFullscreenParams,
  updateDocQueryString,
} from './fullscreen-restore';
import { focusPersonOnOpen } from './presence-focus';

function withoutFullscreenDatabaseSelection(
  selector: EditorSelector | undefined,
  fullscreenDatabaseId: string | undefined
): EditorSelector | undefined {
  if (!selector || !fullscreenDatabaseId) {
    return selector;
  }

  const next: EditorSelector = { ...selector };
  if (next.blockIds) {
    const blockIds = next.blockIds.filter(id => id !== fullscreenDatabaseId);
    if (blockIds.length > 0) {
      next.blockIds = blockIds;
    } else {
      delete next.blockIds;
    }
  }
  if (next.elementIds) {
    const elementIds = next.elementIds.filter(
      id => id !== fullscreenDatabaseId && !id.startsWith('file-index-')
    );
    if (elementIds.length > 0) {
      next.elementIds = elementIds;
    } else {
      delete next.elementIds;
    }
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

const FULLSCREEN_QUERY_KEYS = [
  'fullscreenDatabaseId',
  'fullscreenDatabaseRowId',
  'fullscreenDatabaseRowShardDocId',
  'fullscreenDatabaseColumnId',
  'fullscreenAttachmentId',
  'fullscreenAttachmentPageIndex',
  'fullscreenAttachmentQuery',
  'fullscreenAttachmentHighlightRects',
  'fullscreenAttachmentRequestId',
  'fullscreenViewId',
  'fullscreenWorkflowId',
] as const;

/**
 * Editor Entity
 *
 * Uses PresentationStateMachine as SINGLE SOURCE OF TRUTH for all
 * presentation-related state (moderation, following, frame navigation).
 */
export class Editor extends Entity {
  readonly scope = this.framework.createScope(EditorScope, {
    editor: this as Editor,
  });

  readonly mode$ = new LiveData<DocMode>(DEFAULT_DOC_MODE);
  readonly selector$ = new LiveData<EditorSelector | undefined>(undefined);
  readonly doc = this.docService.doc;
  readonly isSharedMode =
    this.workspaceService.workspace.openOptions.isSharedMode;
  readonly editorContainer$ = new LiveData<AffineEditorContainer | null>(null);
  /** Whether an editor (with or without page container) is bound. */
  private hostBound = false;
  /** The editor bound without page container, as the canvas of a workflow. */
  private boundHost: EditorHost | null = null;
  readonly defaultOpenProperty$ = new LiveData<DefaultOpenProperty | undefined>(
    undefined
  );
  workbenchView: WorkbenchView | null = null;

  private readonly focusAt$ = LiveData.computed(get => {
    const selector = get(this.selector$);
    const mode = get(this.mode$);
    let commentId = selector?.commentId;
    let key: 'blockIds' | 'elementIds' = 'blockIds';
    let ids = selector?.blockIds ?? [];

    if (mode === 'edgeless') {
      const elementIds = selector?.elementIds ?? [];
      if (elementIds.length) {
        ids = elementIds;
        key = 'elementIds';
      }
    }

    if (!ids.length && !commentId && !selector?.presenceUserId) return null;

    return {
      id: ids[0],
      ids,
      key,
      mode,
      refreshKey: selector?.refreshKey,
      presenceUserId: selector?.presenceUserId,
      commentId: commentId,
      activityRunId: selector?.activityRunId,
      activityLabel: selector?.activityLabel,
    };
  });

  isPresenting$ = new LiveData<boolean>(false);

  // Delegate to StateMachine (Single Source of Truth)
  get isModerating$() {
    return this.stateMachine.isModerating$;
  }

  // sl-9qm.19: Internal View Mode State
  readonly viewMode$ = new LiveData<boolean>(false);

  updateQueryString(...args: Parameters<WorkbenchView['updateQueryString']>) {
    updateDocQueryString(this.workbenchView, this.doc.id, ...args);
  }

  openViewMode() {
    this.viewMode$.next(true);
    this.updateQueryString({ viewMode: 'true' }, { replace: false });
  }

  closeViewMode() {
    this.viewMode$.next(false);
    this.updateQueryString({ viewMode: undefined }, { replace: true });
  }

  // sl-dpq2: Workflow Fullscreen State
  readonly fullscreenWorkflow$ = new LiveData<{
    workflowId: string;
    workflowName: string;
  } | null>(null);

  openWorkflowFullscreen(workflowId: string, workflowName: string) {
    this.fullscreenWorkflow$.next({ workflowId, workflowName });
    this.updateQueryString(
      { fullscreenWorkflowId: workflowId },
      // Push history entry so browser Back closes fullscreen via URL state.
      { replace: false }
    );
  }

  closeWorkflowFullscreen() {
    this.fullscreenWorkflow$.next(null);
    this.updateQueryString(
      { fullscreenWorkflowId: undefined },
      { replace: true }
    );
  }

  /** The bound editor, with or without page container around it. */
  private get editorHost() {
    return this.editorContainer$.value?.host ?? this.boundHost;
  }

  async togglePresentation() {
    const gfx = this.editorHost?.std.get(GfxControllerIdentifier);
    if (!gfx) return;

    const state = this.stateMachine.state$.value;
    if (!this.isPresenting$.value && state.type.startsWith('following')) {
      console.warn('[Editor] Cannot start presentation while following');
      return;
    }

    if (!this.isPresenting$.value) {
      await gfx.std.get(EdgelessFrameManagerIdentifier).loadFrames();
      if (this.stateMachine.state$.value.type.startsWith('following')) return;
      gfx.tool.setTool(PresentTool);
    } else {
      gfx.tool.setTool(DefaultTool);
    }
  }

  /**
   * Result of pre-moderation check
   */
  readonly lastModerationCheckResult$ = new LiveData<{
    success: boolean;
    error?: 'offline' | 'moderator_exists';
    existingModeratorName?: string;
  } | null>(null);

  /**
   * Freshness threshold for moderator heartbeat (ms)
   * If heartbeat is older than this, moderator is considered stale/disconnected
   */
  private static readonly MODERATOR_FRESHNESS_MS = 2000;

  /**
   * Check if moderation can be started
   * 1. Internet connectivity check
   * 2. Existing moderator check (with freshness validation)
   */
  private checkCanStartModeration(): {
    success: boolean;
    error?: 'offline' | 'moderator_exists';
    existingModeratorName?: string;
  } {
    // Step 1: Backend reachability check. Browser network state is only a hint;
    // local dev and local-first flows can reach the backend while navigator is
    // stale or false.
    if (!canAttemptBackendRequest()) {
      console.log('[Editor] Cannot start moderation: offline');
      return { success: false, error: 'offline' };
    }

    // Step 2: Check for existing active moderator (with freshness validation)
    const awareness =
      this.docService.doc.blockSuiteDoc.awarenessStore.awareness;
    const states = awareness.getStates();
    const myClientId = awareness.clientID;
    const now = Date.now();

    for (const [clientId, state] of states) {
      // Skip ourselves
      if (clientId === myClientId) continue;

      const typedState = state as {
        presentation?: {
          isPresenting?: boolean;
          heartbeatTs?: number;
        };
        user?: { name?: string };
      };

      // Check if this client is presenting
      if (!typedState.presentation?.isPresenting) continue;

      // Check freshness: heartbeat must be within threshold
      // If no heartbeatTs (old version) or heartbeat is fresh, block
      const heartbeatTs = typedState.presentation.heartbeatTs;
      const isFresh =
        !heartbeatTs || now - heartbeatTs < Editor.MODERATOR_FRESHNESS_MS;
      if (isFresh) {
        const moderatorName = typedState.user?.name || 'Someone';
        return {
          success: false,
          error: 'moderator_exists',
          existingModeratorName: moderatorName,
        };
      }
    }

    return { success: true };
  }

  /**
   * Toggle Moderation Mode
   * Uses StateMachine for state management (Single Source of Truth)
   *
   * Performs pre-checks before starting:
   * 1. Internet connectivity
   * 2. No existing active moderator (with freshness validation)
   *
   * sl-ck8v: Now async due to socket communication
   *
   * @returns Check result (success/error) - also emitted to lastModerationCheckResult$
   */
  toggleModeration(): {
    success: boolean;
    error?: 'offline' | 'moderator_exists';
    existingModeratorName?: string;
  } {
    const gfx = this.editorHost?.std.get(GfxControllerIdentifier);

    const state = this.stateMachine.state$.value;

    if (state.type === 'idle') {
      // Pre-check before starting moderation
      const checkResult = this.checkCanStartModeration();
      this.lastModerationCheckResult$.next(checkResult);

      if (!checkResult.success) {
        return checkResult;
      }

      // Start moderation (async - fire and forget)
      // sl-ck8v: Method is now async, we handle errors via socket events
      this.stateMachine
        .startModeration()
        .then(result => {
          if (result.success && gfx) {
            // Broadcast initial viewport
            const bounds = gfx.viewport.viewportBounds;
            this.stateMachine.broadcastViewport({
              zoom: gfx.viewport.zoom,
              centerX: gfx.viewport.centerX,
              centerY: gfx.viewport.centerY,
              visibleBounds: {
                x: bounds.x,
                y: bounds.y,
                w: bounds.w,
                h: bounds.h,
              },
            });
          }
        })
        .catch(console.error);
      return { success: true }; // Optimistic response
    } else if (state.type.startsWith('moderating')) {
      // Stop moderation - exit presentation first if active
      if (this.isPresenting$.value) {
        gfx?.tool.setTool(DefaultTool);
      }
      // sl-ck8v: Method is now async, fire and forget
      this.stateMachine.stopModeration().catch(console.error);
      this.lastModerationCheckResult$.next(null);
      return { success: true };
    }

    // Cannot toggle moderation while following (guard in StateMachine)
    return { success: false };
  }

  setSelector(selector: EditorSelector | undefined) {
    this.selector$.next(selector);
  }

  toggleMode() {
    this.setMode(this.mode$.value === 'edgeless' ? 'page' : 'edgeless', false);
    this.persistMode();
  }

  setMode(mode: DocMode, persist = false) {
    // Any explicit top mode selection exits overlay modes.
    if (this.fullscreenWorkflow$.value) {
      this.closeWorkflowFullscreen();
    }
    if (this.viewMode$.value) {
      this.closeViewMode();
    }
    this.mode$.next(mode);
    if (persist) {
      this.doc.setPrimaryMode(mode);
    }
  }

  /**
   * Save the current mode to DocViewState (localStorage).
   * Called ONLY by explicit user actions (mode switch buttons),
   * never by hydration, URL sync, or lifecycle events.
   */
  persistMode() {
    this.docViewState.update(this.doc.id, { mode: this.mode$.value });
  }

  setDefaultOpenProperty(defaultOpenProperty: DefaultOpenProperty | undefined) {
    this.defaultOpenProperty$.next(defaultOpenProperty);
  }

  /**
   * Sync editor params with view query string
   */
  bindWorkbenchView(view: WorkbenchView) {
    if (this.workbenchView) {
      throw new Error('already bound');
    }
    this.workbenchView = view;

    // Close stale fullscreen from previous doc (registry is workspace-scoped,
    // survives doc navigation). Will be re-opened below if THIS doc has saved
    // fullscreen state.
    if (
      this.fullscreenRegistry.activeView$.value ||
      this.fullscreenRegistry.getPendingOpen()
    ) {
      this.fullscreenRegistry.close();
    }

    // ─── Restore from DocViewStateService (single source of truth) ───
    const savedState = this.docViewState.get(this.doc.id);

    const stablePrimaryMode = this.doc.getPrimaryMode();
    const defaultMode =
      savedState?.mode || stablePrimaryMode || DEFAULT_DOC_MODE;

    // ─── Two-layer URL parsing ───
    // rawParams$: URL params as-is (mode can be undefined if URL has no ?mode=)
    // viewParams$: with defaults applied (mode is always defined)
    //
    // The subscription uses rawParams$ to detect whether mode came from the URL
    // vs. from the default. This prevents navigation-away URL changes (which
    // clear ?mode=) from resetting the editor's mode.
    const rawParams$ = view
      .queryString$<
        ReferenceParams & { refreshKey?: string }
      >(paramsParseOptions)
      .map(preprocessParams);
    const isBoundToCurrentDoc = () =>
      isWorkbenchPathForDoc(view.history.location.pathname, this.doc.id);

    const restoredInitialFullscreenQuery = createSavedFullscreenQueryPatch(
      rawParams$.value as Record<string, unknown>,
      savedState
    );
    if (restoredInitialFullscreenQuery) {
      this.updateQueryString(restoredInitialFullscreenQuery, { replace: true });
    }

    const viewParams$ = rawParams$.map(params =>
      defaults(mergeSavedFullscreenParams(params, savedState), {
        mode: defaultMode,
      })
    );

    // Set initial mode from URL BEFORE subscriptions start.
    // Without this, the editorParams$ writer fires first with the old
    // defaultMode and overwrites the URL's ?mode= parameter.
    const initialRaw = rawParams$.value;
    if (initialRaw.mode && initialRaw.mode !== this.mode$.value) {
      this.setMode(initialRaw.mode, false);
    }

    const editorParams$ = LiveData.computed(get => {
      const fullscreenWf = get(this.fullscreenWorkflow$);
      const isViewMode = get(this.viewMode$);
      const activeFullscreen = get(this.fullscreenRegistry.activeView$);
      const pendingFullscreen = this.fullscreenRegistry.getPendingOpen();
      const rawParams = get(rawParams$) as Record<string, unknown>;
      // A database-cell search navigation reaches the URL before the
      // fullscreen registry has hydrated. Preserve that explicit, exact
      // row-shard navigation during the transition so the writer cannot erase
      // it before the reader opens the fullscreen database. Requiring both the
      // row and its deterministic shard locator keeps stale fullscreen query
      // params from becoming writer state after an ordinary close.
      const hydratingFocusedDatabaseId =
        typeof rawParams.fullscreenDatabaseId === 'string' &&
        typeof rawParams.fullscreenDatabaseRowId === 'string' &&
        typeof rawParams.fullscreenDatabaseRowShardDocId === 'string'
          ? rawParams.fullscreenDatabaseId
          : undefined;
      // URL fullscreen params are input for the reader/hydration path only.
      // Writer output is projected from the current fullscreen registry state;
      // otherwise stale query params survive after leaving fullscreen. The
      // exact focused-row transition above is the only URL-backed exception.
      const fullscreenDbId =
        activeFullscreen?.viewId === 'database'
          ? activeFullscreen.blockId
          : pendingFullscreen?.viewId === 'database'
            ? pendingFullscreen.blockId
            : hydratingFocusedDatabaseId;
      const urlFullscreenViewId =
        fullscreenDbId && typeof rawParams.fullscreenViewId === 'string'
          ? rawParams.fullscreenViewId
          : undefined;
      const fullscreenDatabaseRowId =
        fullscreenDbId && typeof rawParams.fullscreenDatabaseRowId === 'string'
          ? rawParams.fullscreenDatabaseRowId
          : undefined;
      const fullscreenDatabaseRowShardDocId =
        fullscreenDbId &&
        typeof rawParams.fullscreenDatabaseRowShardDocId === 'string'
          ? rawParams.fullscreenDatabaseRowShardDocId
          : undefined;
      const fullscreenDatabaseColumnId =
        fullscreenDbId &&
        typeof rawParams.fullscreenDatabaseColumnId === 'string'
          ? rawParams.fullscreenDatabaseColumnId
          : undefined;
      const fullscreenAttachmentId =
        activeFullscreen?.viewId === 'attachment'
          ? activeFullscreen.blockId
          : pendingFullscreen?.viewId === 'attachment'
            ? pendingFullscreen.blockId
            : undefined;
      const attachmentNavigationParams = fullscreenAttachmentId
        ? {
            ...(typeof rawParams.fullscreenAttachmentPageIndex === 'string'
              ? {
                  fullscreenAttachmentPageIndex:
                    rawParams.fullscreenAttachmentPageIndex,
                }
              : {}),
            ...(typeof rawParams.fullscreenAttachmentQuery === 'string'
              ? {
                  fullscreenAttachmentQuery:
                    rawParams.fullscreenAttachmentQuery,
                }
              : {}),
            ...(typeof rawParams.fullscreenAttachmentHighlightRects === 'string'
              ? {
                  fullscreenAttachmentHighlightRects:
                    rawParams.fullscreenAttachmentHighlightRects,
                }
              : {}),
            ...(typeof rawParams.fullscreenAttachmentRequestId === 'string'
              ? {
                  fullscreenAttachmentRequestId:
                    rawParams.fullscreenAttachmentRequestId,
                }
              : {}),
          }
        : {};

      const selector = withoutFullscreenDatabaseSelection(
        get(this.selector$),
        fullscreenDbId
      );

      return {
        mode: get(this.mode$),
        ...selector,
        ...(fullscreenDbId ? { fullscreenDatabaseId: fullscreenDbId } : {}),
        ...(urlFullscreenViewId
          ? { fullscreenViewId: urlFullscreenViewId }
          : {}),
        ...(fullscreenDatabaseRowId ? { fullscreenDatabaseRowId } : {}),
        ...(fullscreenDatabaseRowShardDocId
          ? { fullscreenDatabaseRowShardDocId }
          : {}),
        ...(fullscreenDatabaseColumnId ? { fullscreenDatabaseColumnId } : {}),
        ...(fullscreenAttachmentId ? { fullscreenAttachmentId } : {}),
        ...attachmentNavigationParams,
        ...(fullscreenWf
          ? { fullscreenWorkflowId: fullscreenWf.workflowId }
          : {}),
        ...(isViewMode ? { viewMode: 'true' } : {}),
      };
    });

    let updating = false;

    const unsubscribeViewParams = viewParams$.subscribe(params => {
      if (updating) return;
      if (!isBoundToCurrentDoc()) return;
      updating = true;
      try {
        const editorParams = editorParams$.value;
        const raw = rawParams$.value;

        // Only change mode if the URL EXPLICITLY contains ?mode=.
        // When navigation removes ?mode= (e.g. sidebar click), the mode stays
        // as-is. This prevents the captured defaultMode from resetting the
        // user's session mode during navigation transitions.
        if (raw.mode && params.mode !== editorParams.mode) {
          this.setMode(params.mode, false);
        }

        const selector = withoutFullscreenDatabaseSelection(
          omit(params, [
            'mode',
            'fullscreenDatabaseId',
            'fullscreenDatabaseRowId',
            'fullscreenDatabaseRowShardDocId',
            'fullscreenDatabaseColumnId',
            'fullscreenAttachmentId',
            'fullscreenAttachmentPageIndex',
            'fullscreenAttachmentQuery',
            'fullscreenAttachmentHighlightRects',
            'fullscreenAttachmentRequestId',
            'fullscreenViewId',
            'fullscreenWorkflowId',
            'viewMode',
          ]),
          (params as { fullscreenDatabaseId?: string }).fullscreenDatabaseId
        );
        const editorSelectorParams = omit(editorParams, [
          'mode',
          'fullscreenDatabaseId',
          'fullscreenDatabaseRowId',
          'fullscreenDatabaseRowShardDocId',
          'fullscreenDatabaseColumnId',
          'fullscreenAttachmentId',
          'fullscreenAttachmentPageIndex',
          'fullscreenAttachmentQuery',
          'fullscreenAttachmentHighlightRects',
          'fullscreenAttachmentRequestId',
          'fullscreenViewId',
          'fullscreenWorkflowId',
          'viewMode',
        ]);
        if (!isEqual(selector, editorSelectorParams)) {
          this.setSelector(selector);
        }

        // Sync database fullscreen from URL (state-based, replaces setTimeout hack)
        const fullscreenDbId = (params as { fullscreenDatabaseId?: string })
          .fullscreenDatabaseId;
        const currentDbView = this.fullscreenRegistry.activeView$.value;
        const pendingDbView = this.fullscreenRegistry.getPendingOpen();
        const currentDbId =
          currentDbView?.viewId === 'database'
            ? currentDbView.blockId
            : pendingDbView?.viewId === 'database'
              ? pendingDbView.blockId
              : null;
        if (fullscreenDbId !== (currentDbId ?? undefined)) {
          if (fullscreenDbId) {
            this.fullscreenRegistry.open('database', fullscreenDbId);
          } else if (
            currentDbView?.viewId === 'database' ||
            pendingDbView?.viewId === 'database'
          ) {
            this.fullscreenRegistry.close();
          }
        }

        const fullscreenAttachmentId = (
          params as { fullscreenAttachmentId?: string }
        ).fullscreenAttachmentId;
        const currentAttachmentId =
          currentDbView?.viewId === 'attachment'
            ? currentDbView.blockId
            : pendingDbView?.viewId === 'attachment'
              ? pendingDbView.blockId
              : null;
        if (fullscreenAttachmentId !== (currentAttachmentId ?? undefined)) {
          if (fullscreenAttachmentId) {
            this.fullscreenRegistry.open('attachment', fullscreenAttachmentId);
          } else if (
            currentDbView?.viewId === 'attachment' ||
            pendingDbView?.viewId === 'attachment'
          ) {
            this.fullscreenRegistry.close();
          }
        }

        // sl-dpq2: Sync workflow fullscreen from URL
        const fullscreenWfId = (params as { fullscreenWorkflowId?: string })
          .fullscreenWorkflowId;
        const currentWfId = this.fullscreenWorkflow$.value?.workflowId ?? null;
        if (fullscreenWfId !== currentWfId) {
          if (fullscreenWfId) {
            this.fullscreenWorkflow$.next({
              workflowId: fullscreenWfId,
              workflowName: '',
            });
          } else {
            this.fullscreenWorkflow$.next(null);
          }
        }

        // sl-9qm.19: Sync view mode from URL
        const viewModeParam = (params as { viewMode?: string }).viewMode;
        const isViewMode = viewModeParam === 'true';
        if (isViewMode !== this.viewMode$.value) {
          this.viewMode$.next(isViewMode);
        }
      } finally {
        updating = false;
      }
    });

    // ─── Initial hydration ───
    // Apply mode from URL or savedState ONCE at bind time.
    // This is separate from the subscription above because the initial load
    // MUST apply the default even when the URL has no explicit ?mode=.
    const initialMode = viewParams$.value?.mode ?? defaultMode;
    updating = true;
    try {
      this.setMode(initialMode as DocMode, false);
      if (!initialRaw.mode && !savedState?.mode) {
        this.updateQueryString(
          { mode: initialMode as DocMode },
          { replace: true }
        );
      }

      const initialViewParams = viewParams$.value;
      if (initialViewParams) {
        const initialFullscreenDbId = (
          initialViewParams as { fullscreenDatabaseId?: string }
        ).fullscreenDatabaseId;
        const initialFullscreenAttachmentId = (
          initialViewParams as { fullscreenAttachmentId?: string }
        ).fullscreenAttachmentId;
        const fullscreenWfId = (
          initialViewParams as { fullscreenWorkflowId?: string }
        ).fullscreenWorkflowId;

        if (initialFullscreenDbId) {
          this.fullscreenRegistry.open('database', initialFullscreenDbId);
        } else if (initialFullscreenAttachmentId) {
          this.fullscreenRegistry.open(
            'attachment',
            initialFullscreenAttachmentId
          );
        }

        if (fullscreenWfId) {
          this.fullscreenWorkflow$.next({
            workflowId: fullscreenWfId,
            workflowName: '',
          });
        }

        // sl-9qm.19: Hydrate view mode from URL
        const viewModeParam = (initialViewParams as { viewMode?: string })
          .viewMode;
        if (viewModeParam === 'true') {
          this.viewMode$.next(true);
        }
      }

      // Restore fullscreen from DocViewStateService (sidebar navigation return).
      // URL params handle F5 reload; DocViewState handles sidebar return where
      // URL params have been cleared by the previous cleanup.
      if (
        savedState?.fullscreen &&
        !this.fullscreenRegistry.activeView$.value
      ) {
        const raw = rawParams$.value as Record<string, unknown>;
        const urlHasFullscreen =
          raw.fullscreenDatabaseId ||
          raw.fullscreenAttachmentId ||
          raw.fullscreenWorkflowId;
        if (!urlHasFullscreen) {
          this.fullscreenRegistry.open(
            savedState.fullscreen.viewId,
            savedState.fullscreen.blockId
          );
        }
      }
    } finally {
      updating = false;
    }

    const unsubscribeEditorParams = editorParams$.subscribe(params => {
      if (updating) return;
      if (!isBoundToCurrentDoc()) return;
      updating = true;
      try {
        if (!isEqual(params, viewParams$.value)) {
          const newQueryString: Partial<Record<string, string | undefined>> =
            Object.fromEntries(
              FULLSCREEN_QUERY_KEYS.map(key => [key, undefined])
            );

          Object.entries(params).forEach(([k, v]) => {
            newQueryString[k] = Array.isArray(v) ? v.join(',') : String(v);
          });

          this.updateQueryString(newQueryString, { replace: true });
        }
      } finally {
        updating = false;
      }
    });

    return () => {
      this.workbenchView = null;
      unsubscribeEditorParams.unsubscribe();
      unsubscribeViewParams.unsubscribe();
    };
  }

  private cancelPresenceFocus: (() => void) | null = null;

  handleFocusAt(focusAt: {
    key: 'blockIds' | 'elementIds';
    mode: DocMode;
    id?: string;
    ids?: string[];
    commentId?: string;
    activityRunId?: string;
    activityLabel?: string;
    presenceUserId?: string;
  }): Promise<void> | undefined {
    const editorContainer = this.editorContainer$.value;
    if (!editorContainer) return undefined;

    this.cancelPresenceFocus?.();
    this.cancelPresenceFocus = null;
    if (focusAt.presenceUserId) {
      if (this.workbenchView && focusAt.mode === 'edgeless') {
        const focus = focusPersonOnOpen(
          editorContainer,
          this.doc.blockSuiteDoc.awarenessStore,
          this.doc.id,
          focusAt.presenceUserId,
          this.presenceSettings.sharePreview.presenceDiscoveryEnabled$,
          signal =>
            this.presenceSettings.sharePreview.revalidatePresencePolicy(signal)
        );
        this.cancelPresenceFocus = focus;
        return focus.ready;
      }
      return undefined;
    }
    const selection = editorContainer.host?.std.selection;
    const { id, key, mode, commentId } = focusAt;

    let finalId = id;
    let finalKey = key;
    let finalIds = focusAt.ids?.length ? [...focusAt.ids] : id ? [id] : [];
    let highlight = true;

    if (commentId && !id && editorContainer.host?.std) {
      const std = editorContainer.host.std;

      const inlineCommentedSelections = findCommentedTexts(
        std.store,
        commentId
      );
      if (inlineCommentedSelections.length > 0) {
        const firstSelection = inlineCommentedSelections[0];
        finalId = firstSelection.from.blockId;
        finalIds = [finalId];
        finalKey = 'blockIds';
      } else {
        const blockCommentedBlocks = findCommentedBlocks(std.store, commentId);
        if (blockCommentedBlocks.length > 0) {
          finalId = blockCommentedBlocks[0].id;
          finalIds = [finalId];
          finalKey = 'blockIds';
        } else {
          const commentedElements = findCommentedElements(std.store, commentId);
          if (commentedElements.length > 0) {
            finalId = commentedElements[0].id;
            finalIds = [finalId];
            finalKey = 'elementIds';
          }
        }
      }
      selection?.clear();

      setTimeout(() => {
        const commentManager = this.framework.get(DocCommentManagerService);
        const commentPanelService = this.framework.get(CommentPanelService);
        const commentEntity = commentManager.get(this.doc.id);
        commentPanelService.openCommentPanel();
        commentEntity.obj.highlightComment(commentId);
        commentEntity.release();
      }, 0);

      highlight = false;
    }

    if (finalIds.length) {
      // Switch mode if needed (edgeless ↔ page)
      const needsModeSwitch = this.mode$.value !== mode;
      if (needsModeSwitch) {
        this.setMode(mode, false);
      }

      // Delegate ALL navigation to the scroll-anchoring widget via HighlightSelection.
      // The widget handles both page (scrollIntoView) and edgeless (viewport.setCenter)
      // navigation state-based: it subscribes to blockUpdated events and waits until
      // the block is actually rendered before navigating. No setTimeout polling needed.
      //
      // For mode switches, we wait one frame for the editor to re-render before
      // setting the selection — otherwise the widget hasn't mounted yet.
      const setHighlight = () => {
        const sel = this.editorContainer$.value?.host?.std.selection;
        if (!sel) return false; // Not ready yet
        sel.setGroup('scene', [
          sel.create(HighlightSelection, {
            mode,
            [finalKey]: finalIds,
            highlight,
            ...(focusAt.activityRunId
              ? { activityRunId: focusAt.activityRunId }
              : {}),
            ...(focusAt.activityLabel
              ? { activityLabel: focusAt.activityLabel }
              : {}),
          } as const),
        ]);
        return true;
      };

      // Try immediately — works for same-doc same-mode navigation.
      // If editor not mounted yet (cross-doc or mode switch), wait for
      // editorContainer$ to become available, then set the highlight.
      if (!needsModeSwitch && setHighlight()) {
        // Success — no waiting needed
      } else {
        // Wait for editor container to be ready (state-based, no setTimeout)
        const sub = this.editorContainer$.subscribe(container => {
          if (!container?.host) return;
          // Give the widget one frame to mount after container is ready
          requestAnimationFrame(() => {
            if (setHighlight()) {
              sub.unsubscribe();
            }
          });
        });
      }
    }
    return undefined;
  }

  bindEditorContainer(
    editorContainer: AffineEditorContainer,
    docTitle?: DocTitle | null,
    scrollViewport?: HTMLElement | null
  ) {
    if (this.editorContainer$.value) {
      throw new Error('already bound');
    }

    this.editorContainer$.next(editorContainer);
    const unbindHost = this.bindEditorHost(
      editorContainer.host,
      docTitle,
      scrollViewport
    );
    const unbind = () => {
      unbindHost();
      this.editorContainer$.next(null);
    };
    return Object.assign(unbind, { ready: unbindHost.ready });
  }

  /**
   * Binds the editor of the document without the page container around it,
   * as the canvas of a workflow does: presenting, following, fullscreen and
   * the active document of the workspace come with it. A canvas without
   * frames presents and follows freely.
   */
  bindEditorHost(
    host: EditorHost | null | undefined,
    docTitle?: DocTitle | null,
    scrollViewport?: HTMLElement | null
  ) {
    if (this.hostBound) {
      throw new Error('already bound');
    }
    this.hostBound = true;
    this.boundHost = host ?? null;
    const unsubs: (() => void)[] = [];

    const gfx = host?.std.get(GfxControllerIdentifier);
    const std = host?.std;
    const slots = std?.getOptional(EdgelessLegacySlotIdentifier) ?? undefined;
    const frameManager =
      std?.getOptional(EdgelessFrameManagerIdentifier) ?? undefined;

    // ═══════════════════════════════════════════════════════════════════════
    // RESTORE scroll position in page mode.
    // SAVE scroll via throttled listener (user action, not cleanup).
    // Edgeless viewport restored by BlockSuite natively.
    // ═══════════════════════════════════════════════════════════════════════
    const savedState = this.docViewState.get(this.doc.id);

    if (this.mode$.value === 'page' && savedState?.page && scrollViewport) {
      const target = savedState.page.scrollTop;
      if (target > 0) {
        const attempt = () => {
          scrollViewport.scrollTo(0, target);
          return Math.abs(scrollViewport.scrollTop - target) < 2;
        };
        if (!attempt()) {
          const ro = new ResizeObserver(() => {
            if (attempt()) {
              ro.disconnect();
              clearTimeout(t);
            }
          });
          ro.observe(scrollViewport);
          const t = setTimeout(() => ro.disconnect(), 3000);
          unsubs.push(() => {
            ro.disconnect();
            clearTimeout(t);
          });
        }
      }
    }

    // Scroll save: throttled listener (user action, never cleanup).
    if (scrollViewport) {
      const saveScroll = throttle(() => {
        this.docViewState.update(this.doc.id, {
          page: { scrollTop: scrollViewport.scrollTop },
        });
      }, 500);
      scrollViewport.addEventListener('scroll', saveScroll, { passive: true });
      unsubs.push(() => {
        scrollViewport.removeEventListener('scroll', saveScroll);
        saveScroll.cancel();
      });
    }

    // Skip title focus if restoring a saved scroll position — focusEnd()
    // scrolls the viewport to the top, overriding the restored position.
    const restoringScroll = !!(
      savedState?.page?.scrollTop && savedState.page.scrollTop > 0
    );

    const initialFocusAt = this.focusAt$.value;
    let initialFocusReady: Promise<void> | undefined;
    if (initialFocusAt) {
      // Navigate to the target block (works for both page and edgeless mode).
      // The scroll-anchoring widget handles the actual navigation state-based.
      initialFocusReady = this.handleFocusAt(initialFocusAt);
    } else if (this.mode$.value === 'page' && !restoringScroll) {
      // No focus target — focus the title in page mode
      const title = docTitle?.querySelector<
        HTMLElement & { inlineEditor: InlineEditor | null }
      >('rich-text');
      if (BUILD_CONFIG.isMobileEdition) {
        const titleText = this.doc.title$.value;
        if (!titleText?.length) {
          title?.inlineEditor?.focusEnd();
        }
      } else {
        title?.inlineEditor?.focusEnd();
      }
    }

    // Mode is saved in setMode() via simple localStorage key.
    // Edgeless viewport is handled by BlockSuite natively.
    // Scroll position is not persisted (browser handles it on reload).

    const subscription = this.focusAt$
      .distinctUntilChanged(
        (a, b) =>
          a?.id === b?.id &&
          a?.key === b?.key &&
          a?.refreshKey === b?.refreshKey &&
          a?.presenceUserId === b?.presenceUserId &&
          a?.activityRunId === b?.activityRunId &&
          a?.activityLabel === b?.activityLabel
      )
      .pipe(skip(1))
      .subscribe(anchor => {
        if (!anchor) {
          this.cancelPresenceFocus?.();
          this.cancelPresenceFocus = null;
          return;
        }
        void this.handleFocusAt(anchor);
      });
    unsubs.push(subscription.unsubscribe.bind(subscription));

    // ═══════════════════════════════════════════════════════════════════════
    // PRESENTING STATE
    // ═══════════════════════════════════════════════════════════════════════

    const editorMode = std?.get(DocModeProvider)?.getEditorMode();
    if (!editorMode || editorMode !== 'edgeless' || !gfx) {
      this.isPresenting$.next(false);
    } else {
      this.isPresenting$.next(
        gfx.tool.currentToolName$.peek() === 'frameNavigator'
      );

      const disposable = effect(() => {
        this.isPresenting$.next(
          gfx.tool.currentToolName$.value === 'frameNavigator'
        );
      });
      unsubs.push(disposable);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // VIEWPORT BROADCASTING (MODERATOR - FREE MODE)
    // ═══════════════════════════════════════════════════════════════════════

    // Broadcast viewport to followers at 60fps (16ms)
    // Higher rate = smoother following, more network traffic but worth it
    const broadcastViewport = throttle(
      (viewport: ViewportState) => {
        this.stateMachine.broadcastViewport(viewport);
      },
      16,
      { leading: true, trailing: true }
    );

    if (gfx) {
      const viewportSubscription = gfx.viewport.viewportUpdated.subscribe(
        update => {
          const bounds = gfx.viewport.viewportBounds;
          broadcastViewport({
            zoom: update.zoom,
            centerX: update.center[0],
            centerY: update.center[1],
            visibleBounds: {
              x: bounds.x,
              y: bounds.y,
              w: bounds.w,
              h: bounds.h,
            },
          });
        }
      );
      unsubs.push(() => {
        viewportSubscription.unsubscribe();
        broadcastViewport.cancel();
      });
    }

    // ═══════════════════════════════════════════════════════════════════════
    // FRAME MODE MANAGEMENT (MODERATOR)
    // ═══════════════════════════════════════════════════════════════════════

    const presentingSubscription = this.isPresenting$.subscribe(
      isPresenting => {
        const state = this.stateMachine.state$.value;
        const isModerating = state.type.startsWith('moderating');

        if (
          isPresenting &&
          frameManager &&
          gfx &&
          !state.type.startsWith('following')
        ) {
          // PresentTool activated → navigate local presentations and
          // moderation sessions through the same animated frame path.
          const frames = frameManager.frames;
          if (frames.length === 0) return;

          // Find frame at viewport center
          const initialFrameIndex = this.findFrameAtViewport(
            frameManager,
            gfx.viewport.centerX,
            gfx.viewport.centerY
          );
          const initialFrame = frames[initialFrameIndex];

          if (isModerating) {
            // Transition moderation to frame mode and broadcast the frame.
            this.stateMachine.enterFrameMode(
              initialFrameIndex,
              initialFrame.id,
              frames.length
            );
          }

          // Send to toolbar
          if (slots) {
            slots.frameIndexChanged.next(initialFrameIndex);
          }
        } else if (!isPresenting && state.type === 'moderating_frames') {
          // PresentTool deactivated while in frame mode → Exit Frame Mode
          this.stateMachine.exitFrameMode();

          // Broadcast current viewport for free mode
          if (gfx) {
            const bounds = gfx.viewport.viewportBounds;
            this.stateMachine.broadcastViewport({
              zoom: gfx.viewport.zoom,
              centerX: gfx.viewport.centerX,
              centerY: gfx.viewport.centerY,
              visibleBounds: {
                x: bounds.x,
                y: bounds.y,
                w: bounds.w,
                h: bounds.h,
              },
            });
          }
        }
      }
    );
    unsubs.push(() => presentingSubscription.unsubscribe());

    // ═══════════════════════════════════════════════════════════════════════
    // FRAME NAVIGATION (LOCAL + MODERATOR, NOT FOLLOWER)
    // ═══════════════════════════════════════════════════════════════════════

    if (slots && frameManager) {
      // Handle frame change from PresentationToolbar (navigatorFrameChanged)
      // Works for local presentation AND moderation, but NOT for followers
      const frameChangedSubscription = slots.navigatorFrameChanged.subscribe(
        frame => {
          const state = this.stateMachine.state$.value;

          // BLOCK: Followers cannot navigate - they follow the moderator
          if (state.type.startsWith('following')) return;

          const frames = frameManager.frames;
          const frameIndex = frames.findIndex(f => f.id === frame.id);
          if (frameIndex < 0) return;

          // If moderating, update StateMachine (broadcasts to followers)
          if (state.type === 'moderating_frames') {
            this.stateMachine.setFrameIndex(frameIndex, frame.id);
          }
          // Local presentation: just navigate, no state machine update needed
        }
      );
      unsubs.push(() => frameChangedSubscription.unsubscribe());

      // Handle user-requested frame change (Next/Prev buttons)
      // Works for local presentation AND moderation, but NOT for followers
      const userRequestedFrameChangeSubscription =
        slots.userRequestedFrameChange.subscribe(requestedIndex => {
          const state = this.stateMachine.state$.value;

          // BLOCK: Followers cannot navigate - they follow the moderator
          if (state.type.startsWith('following')) return;

          const frames = frameManager.frames;
          if (requestedIndex < 0 || requestedIndex >= frames.length) return;

          const frame = frames[requestedIndex];

          // If moderating, update StateMachine (broadcasts to followers)
          if (state.type === 'moderating_frames') {
            this.stateMachine.setFrameIndex(requestedIndex, frame.id);
          }

          // Send to toolbar (for local and moderators only)
          slots.frameIndexChanged.next(requestedIndex);
        });
      unsubs.push(() => userRequestedFrameChangeSubscription.unsubscribe());

      // Handle settings changes
      const settingsSubscription = slots.navigatorSettingUpdated.subscribe(
        settings => {
          const state = this.stateMachine.state$.value;
          if (!state.type.startsWith('moderating')) return;

          this.stateMachine.updateSettings({
            blackBackground: settings.blackBackground,
            fillScreen: settings.fillScreen,
            hideToolbar: settings.hideToolbar,
          });
        }
      );
      unsubs.push(() => settingsSubscription.unsubscribe());

      // ═══════════════════════════════════════════════════════════════════════
      // UNIFIED PRESENTATION ACTIVATION (same code path for moderator & follower)
      // ═══════════════════════════════════════════════════════════════════════

      /**
       * Activates presentation at a specific frame.
       * SAME CODE PATH for:
       * - "Start presentation from here" (Moderator)
       * - "Join presentation" (Follower)
       *
       * The only difference is which state machine method is called.
       */
      const activatePresentationAtFrame = async (
        frameIndex: number,
        frameId: string,
        isFollower: boolean
      ) => {
        await frameManager.loadFrames();
        const frames = frameManager.frames;
        frameIndex = frames.findIndex(frame => frame.id === frameId);
        if (frameIndex < 0 || frameIndex >= frames.length) {
          console.warn('[Editor] Invalid frame index:', frameIndex);
          return;
        }

        console.log(
          '[Editor] Activating presentation at frame:',
          frameIndex,
          isFollower ? '(follower)' : '(moderator)'
        );

        // Step 1: Activate PresentTool FIRST
        if (gfx) {
          gfx.tool.setTool(PresentTool);
        }

        // Step 2: Small delay for PresentTool to initialize
        setTimeout(() => {
          // Step 3: Update state machine
          if (isFollower) {
            this.stateMachine.joinFrameMode(frameIndex, frameId);
          } else {
            this.stateMachine.enterFrameMode(
              frameIndex,
              frameId,
              frames.length
            );
          }

          // Step 4: Send frame to toolbar (CRITICAL - same for both!)
          slots.frameIndexChanged.next(frameIndex);
        }, 50);
      };

      // Handle "Start presentation from here"
      // Two cases:
      // 1. Already moderating (moderating_free) → Enter frame mode (syncs to followers)
      // 2. Not moderating (idle) → Local presentation only (no sync)
      const startPresentationFromFrame = async (
        frameIndex: number,
        frameId: string
      ): Promise<void> => {
        await frameManager.loadFrames();
        frameIndex = frameManager.frames.findIndex(
          frame => frame.id === frameId
        );
        if (frameIndex < 0) return;
        const state = this.stateMachine.state$.value;

        // Cannot start if following someone else
        if (state.type.startsWith('following')) {
          console.warn('[Editor] Cannot start presentation while following');
          return;
        }

        // Case 1: Already moderating → use unified activation (enters frame mode)
        if (state.type.startsWith('moderating')) {
          await activatePresentationAtFrame(frameIndex, frameId, false);
          return;
        }

        // Case 2: Not moderating → local presentation only
        // Just activate the PresentTool and navigate to the frame
        if (gfx) {
          gfx.tool.setTool(PresentTool);
        }

        // Navigate to the requested frame after a small delay for tool init
        setTimeout(() => {
          const frame = frameManager.frames[frameIndex];
          if (frame && gfx) {
            gfx.viewport.setViewportByBound(
              frame.elementBound,
              [20, 20, 20, 20],
              true
            );
          }
          // Send to toolbar
          slots.frameIndexChanged.next(frameIndex);
        }, 50);
      };
      const startPresentationFromFrameSubscription =
        slots.startPresentationFromFrame.subscribe(
          ({ frameIndex, frameId }) => {
            startPresentationFromFrame(frameIndex, frameId).catch(
              console.error
            );
          }
        );
      unsubs.push(() => startPresentationFromFrameSubscription.unsubscribe());

      // Handle "Join presentation at frame" via SLOT (FOLLOWER)
      const joinPresentationAtFrameSubscription =
        slots.joinPresentationAtFrame.subscribe(({ frameIndex, frameId }) => {
          const state = this.stateMachine.state$.value;

          // Can only join if following
          if (!state.type.startsWith('following')) {
            console.warn('[Editor] Cannot join presentation - not following');
            return;
          }

          activatePresentationAtFrame(frameIndex, frameId, true).catch(
            console.error
          );
        });
      unsubs.push(() => joinPresentationAtFrameSubscription.unsubscribe());

      // Handle "Join presentation at frame" via STATE MACHINE REQUEST (from React UI)
      const joinPresentationRequestSubscription =
        this.stateMachine.joinPresentationRequest$.subscribe(request => {
          if (!request) return;

          const state = this.stateMachine.state$.value;

          // Can only join if following
          if (!state.type.startsWith('following')) {
            return;
          }

          activatePresentationAtFrame(
            request.frameIndex,
            request.frameId,
            true
          ).catch(console.error);

          // Clear the request
          this.stateMachine.joinPresentationRequest$.next(null);
        });
      unsubs.push(() => joinPresentationRequestSubscription.unsubscribe());
    }

    // ═══════════════════════════════════════════════════════════════════════
    // FULLSCREEN SERVICE
    // ═══════════════════════════════════════════════════════════════════════

    if (gfx) {
      const viewportElement = gfx.std.get(
        ViewportElementProvider
      ).viewportElement;
      const unbindFullscreen =
        this.fullscreenService.bindElement(viewportElement);
      unsubs.push(unbindFullscreen);

      let lastBroadcastFullscreen: boolean | undefined;
      const syncModeratorFullscreen = () => {
        const isModerating = StateMachineHelpers.isModerating(
          this.stateMachine.state$.value
        );
        if (!isModerating) {
          lastBroadcastFullscreen = undefined;
          return;
        }

        const isFullscreen = this.fullscreenService.isFullscreen$.value;
        if (lastBroadcastFullscreen === isFullscreen) return;
        lastBroadcastFullscreen = isFullscreen;
        this.socketService.setFullscreen(isFullscreen);
      };
      const fullscreenSubscription =
        this.fullscreenService.isFullscreen$.subscribe(syncModeratorFullscreen);
      const moderationStateSubscription = this.stateMachine.state$.subscribe(
        syncModeratorFullscreen
      );
      unsubs.push(() => fullscreenSubscription.unsubscribe());
      unsubs.push(() => moderationStateSubscription.unsubscribe());
    }

    // ═══════════════════════════════════════════════════════════════════════
    // FOLLOWER SERVICES
    // ═══════════════════════════════════════════════════════════════════════

    if (gfx) {
      // Viewport sync
      const unbindViewport = this.followerViewportSync.bindViewportController({
        setViewport: (zoom: number, centerX: number, centerY: number) => {
          gfx.viewport.setViewport(zoom, [centerX, centerY], false);
        },
        getViewportForBounds: bounds => {
          const viewport = gfx.viewport.getFitToScreenData(
            Bound.from(bounds),
            [0, 0, 0, 0],
            undefined,
            0
          );
          return {
            zoom: viewport.zoom,
            centerX: viewport.centerX,
            centerY: viewport.centerY,
          };
        },
        getCurrentViewport: () => ({
          zoom: gfx.viewport.zoom,
          centerX: gfx.viewport.centerX,
          centerY: gfx.viewport.centerY,
        }),
        navigateToFrame: async (index: number) => {
          await frameManager?.loadFrames();
          const frame = frameManager?.frames[index];
          if (frame) {
            gfx.viewport.setViewportByBound(
              frame.elementBound,
              [20, 20, 20, 20],
              true
            );
          }
        },
        transitionTo: () => {}, // Lerp handles this internally
      });
      unsubs.push(unbindViewport);

      // Tool control: a canvas without frames has no frame navigator.
      if (frameManager && slots) {
        const unbindTool = this.followerViewportSync.bindToolController({
          enterPresentationMode: () => {
            frameManager
              .loadFrames()
              .then(() => {
                if (gfx.tool.currentToolName$.peek() !== 'frameNavigator') {
                  gfx.tool.setTool(PresentTool, { followerMode: true });
                }
              })
              .catch(console.error);
          },
          exitPresentationMode: () => {
            if (gfx.tool.currentToolName$.peek() === 'frameNavigator') {
              gfx.tool.setTool(DefaultTool);
            }
          },
          setFrameIndex: (index: number) => {
            slots.frameIndexChanged.next(index);
          },
        });
        unsubs.push(unbindTool);
      }

      // Input blocking - SINGLE SOURCE OF TRUTH for viewport.locked
      // Only FollowerInputService controls viewport.locked based on PresentationStateMachine
      const unbindInput = this.followerInput.bindViewportController({
        setLocked: (locked: boolean) => {
          gfx.viewport.locked = locked;
        },
      });
      unsubs.push(unbindInput);

      const unbindKeyboard = this.followerInput.bindKeyboardHandler();
      unsubs.push(unbindKeyboard);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // REALTIME SOCKET (sl-jmyl.6: WorkspaceScope — setActiveDoc/clearActiveDoc)
    // ═══════════════════════════════════════════════════════════════════════

    // Retain this doc while this renderer is mounted. Page/edgeless renderer
    // transitions can overlap, so only the final release clears presence.
    const docId = this.doc.id;
    const releaseActiveDoc = this.socketService.retainActiveDoc(docId);

    const unbind = () => {
      // Unregister doc — socket stays connected for other docs / sidebar
      releaseActiveDoc();
      this.cancelPresenceFocus?.();
      this.cancelPresenceFocus = null;
      for (const unsub of unsubs) {
        unsub();
      }
      this.boundHost = null;
      this.hostBound = false;
    };
    return Object.assign(unbind, { ready: initialFocusReady });
  }

  /**
   * Find frame index that contains or is closest to given coordinates
   */
  private findFrameAtViewport(
    frameManager: {
      frames: Array<{ id: string; elementBound: { center: [number, number] } }>;
      getFrameFromPoint: (p: [number, number]) => { id: string } | null;
    },
    centerX: number,
    centerY: number
  ): number {
    const frames = frameManager.frames;
    if (frames.length === 0) return 0;

    // Try to find frame containing the point
    const frameAtPoint = frameManager.getFrameFromPoint([centerX, centerY]);
    if (frameAtPoint) {
      const index = frames.findIndex(f => f.id === frameAtPoint.id);
      if (index >= 0) return index;
    }

    // Find closest frame
    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      const frameCenter = frame.elementBound.center;
      const dx = frameCenter[0] - centerX;
      const dy = frameCenter[1] - centerY;
      const distance = dx * dx + dy * dy;

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  constructor(
    private readonly docService: DocService,
    private readonly workspaceService: WorkspaceService,
    private readonly stateMachine: PresentationStateMachine,
    private readonly followerViewportSync: FollowerViewportSyncService,
    private readonly followerInput: FollowerInputService,
    private readonly fullscreenService: FullscreenService,
    private readonly socketService: WorkspaceRealtimeService,
    private readonly docViewState: DocViewStateService,
    private readonly fullscreenRegistry: FullscreenRegistry,
    private readonly presenceSettings: WorkspaceShareSettingService
  ) {
    super();
  }
}
